import { useState } from 'react';
import { BRAND_META } from '../brand';
import type { CalendarDate } from '../types';

/**
 * What is coming, and what is already late to start.
 *
 * Matthew learned World Afro Day was the next day, on the day before
 * (14 September 2026). The lesson taken is that a calendar is only worth
 * having if it leads: every row shows how far away the date is AND
 * whether its preparation window has already opened, and the ones being
 * prepared for sort to the top and say so in words rather than by being
 * a different shade of the same colour.
 */

/** "in 3 days", "today", "started 2 days ago" - a number of days is a
 *  fact, and this is what the fact means. */
function when(d: CalendarDate): string {
  if (d.daysAway === 0) return 'today';
  if (d.daysAway === 1) return 'tomorrow';
  if (d.daysAway < 0) {
    const ago = Math.abs(d.daysAway);
    return d.ends >= d.starts && ago <= 40 ? `running, day ${ago + 1}` : `${ago} days ago`;
  }
  if (d.daysAway < 14) return `in ${d.daysAway} days`;
  return `in ${Math.round(d.daysAway / 7)} weeks`;
}

/**
 * How late the work is, in words.
 *
 * "What do we have to do next week" is answered by the preparation
 * window, not by the date. On 14 September 2026 nothing needed doing the
 * following week and Black History Month was already twenty-five days
 * past the morning its six weeks of lead said to start, while the panel
 * said "in 2 weeks" - true about the date and the opposite of the truth
 * about the work.
 */
function lateness(d: CalendarDate, today: string): string | null {
  if (!d.preparing || d.prepFrom >= today) return null;
  const days = Math.round(
    (Date.parse(`${today}T00:00:00Z`) - Date.parse(`${d.prepFrom}T00:00:00Z`)) / 86_400_000,
  );
  return days > 0 ? `should have started ${days} day${days === 1 ? '' : 's'} ago` : null;
}

export function CalendarPanel({
  dates,
  today,
  onMakeCard,
}: {
  dates: CalendarDate[];
  /** Today in London, from the server, so every surface agrees on it. */
  today: string;
  onMakeCard?: (date: CalendarDate) => void;
}) {
  /**
   * The week is the default because it is the question people actually
   * ask. Ninety days is the planning horizon and it is one click away;
   * opening on it buries the four things that matter under eighty that
   * do not.
   *
   * "This week" means WORK, not dates: anything whose preparation window
   * is already open, plus anything starting within seven days. That is
   * what puts Black History Month in this list in August, which is the
   * entire point of the lead times.
   */
  const [range, setRange] = useState<'week' | 'all'>('week');
  const shown =
    range === 'week' ? dates.filter((d) => d.preparing || (d.daysAway >= 0 && d.daysAway <= 7)) : dates;
  const late = dates.filter((d) => lateness(d, today) !== null).length;

  if (dates.length === 0) {
    return (
      <div className="card">
        <div className="card-head">
          <span className="card-title">Coming up</span>
        </div>
        <p className="muted">
          Nothing in the next ninety days for these brands. Dates live in brand/calendar.json.
        </p>
      </div>
    );
  }

  // Preparing first, then soonest. A date whose window is open is work,
  // and work outranks news however far off the date itself is.
  const ordered = [...shown].sort((a, b) =>
    a.preparing === b.preparing ? a.daysAway - b.daysAway : a.preparing ? -1 : 1,
  );

  return (
    <div className="card">
      <div className="card-head">
        <span className="card-title">Coming up</span>
        <div className="calendar-range" role="tablist" aria-label="How far ahead">
          <button
            role="tab"
            aria-selected={range === 'week'}
            className="brand-tab"
            onClick={() => setRange('week')}
          >
            This week
          </button>
          <button
            role="tab"
            aria-selected={range === 'all'}
            className="brand-tab"
            onClick={() => setRange('all')}
          >
            Next 90 days
          </button>
        </div>
      </div>

      {/* The line that answers the question without being read: how much
          of this is already past the day it should have started. */}
      {late > 0 && (
        <p className="calendar-late-summary">
          {late} {late === 1 ? 'date is' : 'dates are'} past the day their preparation should have
          begun.
        </p>
      )}

      {ordered.length === 0 && (
        <p className="muted">
          Nothing to start this week. Next 90 days holds {dates.length}.
        </p>
      )}

      <ul className="calendar-list">
        {ordered.map((d) => (
          <li key={`${d.id}-${d.starts}`} className={d.preparing ? 'calendar-row preparing' : 'calendar-row'}>
            <span className="calendar-dots" aria-hidden>
              {d.brands.map((b) => (
                <i key={b} style={{ background: BRAND_META[b]?.series ?? 'var(--ink)' }} />
              ))}
            </span>
            <span className="calendar-main">
              <span className="calendar-name">
                {d.url ? (
                  <a href={d.url} target="_blank" rel="noreferrer noopener">
                    {d.name}
                  </a>
                ) : (
                  d.name
                )}
              </span>
              <span className="calendar-meta">
                {d.starts}
                {d.ends !== d.starts ? ` to ${d.ends}` : ''} · {when(d)}
                {/* Named brands, not colour alone: the dots are a
                    reminder for someone who already knows the scheme. */}
                {' · '}
                {d.brands.map((b) => BRAND_META[b]?.label ?? b).join(', ')}
              </span>
              {d.preparing && (
                <span className={lateness(d, today) ? 'calendar-flag late' : 'calendar-flag'}>
                  {lateness(d, today) ?? `Preparing now, ${d.leadDays} days of lead`}
                </span>
              )}
            </span>
            {onMakeCard && (
              <button type="button" className="ghost" onClick={() => onMakeCard(d)}>
                Make a card
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
